// État réactif partagé des « droits d'usage » (entitlement) du compte
// connecté : abonnement actif, super-admin, nombre de boutiques, etc.
//
// L'entitlement vient de GET /api/auth/me (voir utils/firestore-shim.js,
// fetchProfile) puis est enregistré dans localStorage « userinfo » par
// auth/index.js (setProfile). Ce module en expose une copie RÉACTIVE
// (Vue.observable) afin que la sidebar (menu restreint vs complet) et le
// garde de navigation (accès aux pages admin) se mettent à jour dès que
// l'abonnement change — sans passer par le store Vuex, ce qui éviterait
// des imports circulaires entre router, store et auth.
//
// Forme de l'entitlement (api/controllers/accounts.js) :
//   { superadmin, active, planId, planName, maxStores, storeCount, canCreateStore }
import Vue from 'vue';

const USERINFO_KEY = 'userinfo';

// Lit l'entitlement mis en cache dans localStorage : il persiste d'un
// rechargement à l'autre et est présent dès que setProfile() a été appelé.
function readCachedEntitlement() {
    try {
        const info = JSON.parse(localStorage.getItem(USERINFO_KEY)) || {};
        return info.entitlement || null;
    } catch (e) {
        return null;
    }
}

// Objet réactif : tout `computed` qui lit `entitlementState.entitlement`
// est recalculé automatiquement quand setEntitlement() le remplace.
export const entitlementState = Vue.observable({
    entitlement: readCachedEntitlement()
});

// Remplace l'entitlement courant. Appelée par auth/index.js (setProfile,
// Logout) et par le garde de navigation après un fetchProfile.
export function setEntitlement(entitlement) {
    entitlementState.entitlement = entitlement || null;
}

// Renvoie l'entitlement courant (null si inconnu / pas encore chargé).
export function currentEntitlement() {
    return entitlementState.entitlement || null;
}

// Abonnement payé (actif) ou super-admin : accès complet à l'admin.
export function hasPaid(entitlement) {
    const e = entitlement === undefined ? entitlementState.entitlement : entitlement;
    return Boolean(e && (e.superadmin || e.active));
}

// Compte définitivement SANS abonnement actif (ni super-admin). L'accès
// n'est bloqué que dans ce cas précis : tant que l'entitlement est inconnu
// (null), on ne présume pas, pour ne pas verrouiller à tort un compte
// payant dont le profil n'est pas encore chargé.
export function isKnownUnpaid(entitlement) {
    const e = entitlement === undefined ? entitlementState.entitlement : entitlement;
    return Boolean(e && !e.superadmin && !e.active);
}
